'use client'

import { useRouter } from '@/src/i18n/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import LobTabs, { LobTabItem } from './lobTabs'

const HOTELS_INDEX = 1

interface LobTabsWrapperProps {
    /** Resolved server-side by the same rule as the flights homepage, so both landing
     *  pages offer the same verticals on a given domain: AE gets Visa, the rest get
     *  Flights + Hotels where the variant enables it. */
    showVisaTab: boolean
    showFlightsHotelsTab: boolean
}

// Logic layer — resolves the visible tabs and routes each one. Hotels is always active
// here; Flights is an internal route, Visa and Flights + Hotels are still edge-served.
const LobTabsWrapper: React.FC<LobTabsWrapperProps> = ({ showVisaTab, showFlightsHotelsTab }) => {
    const t = useTranslations('header')
    const locale = useLocale()
    const router = useRouter()

    const tabs = useMemo<LobTabItem[]>(() => {
        const items: LobTabItem[] = [
            { key: 'flights', label: t('flights') },
            { key: 'hotels', label: t('hotels') }
        ]
        if (showVisaTab) items.push({ key: 'visa', label: t('visa') })
        else if (showFlightsHotelsTab) items.push({ key: 'fph', label: t('fph') })
        return items
    }, [t, showVisaTab, showFlightsHotelsTab])

    const handleTabChange = useCallback(
        (index: number) => {
            if (index === HOTELS_INDEX) return

            const key = tabs[index]?.key
            if (key === 'flights') {
                router.push('/flights')
                return
            }
            // Neither vertical has a route in this app, so both stay hard navigations —
            // relative, so regional and Cleartrip domains resolve against themselves.
            if (key === 'visa') window.location.href = '/visa'
            if (key === 'fph') window.location.href = `/${locale}/flights-hotels`
        },
        [tabs, router, locale]
    )

    return (
        <LobTabs
            tabs={tabs}
            activeIndex={HOTELS_INDEX}
            onTabChange={handleTabChange}
            ariaLabel="hotel search line of business tabs"
        />
    )
}

export { LobTabsWrapper }
export default LobTabsWrapper
